import { useState, useMemo } from 'react'
import { useList } from '@refinedev/core'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Scan, Play, Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react'

const ROLE_TYPES = [
  'global_administrator',
  'administrator',
  'hr_manager',
  'hiring_manager',
  'recruiter',
  'organization_administrator',
  'organization_hr_manager',
  'organization_hiring_manager',
  'organization_recruiter',
  'organization_user',
  'employee',
]

interface Company {
  id: string
  company_name: string
}

interface MissingEntry {
  category: string
  permissions: string[]
}

interface RoleGap {
  role_type: string
  role_count: number
  missing: MissingEntry[]
}

interface GapReport {
  has_gaps: boolean
  by_role_type: RoleGap[]
}

interface SeedResult {
  roles_updated: number
  total_additions: number
  details: { role_id: string; category: string; added: string[] }[]
}

type Step = 'idle' | 'scanning' | 'review' | 'confirm' | 'seeding' | 'done'

export function PermissionSeedPage() {
  const [step, setStep] = useState<Step>('idle')
  const [roleType, setRoleType] = useState<string>('')
  const [companyId, setCompanyId] = useState<string>('')
  const { data: companiesData } = useList<Company>({
    resource: 'companies',
    pagination: { pageSize: 500 },
    sorters: [{ field: 'company_name', order: 'asc' }],
  })
  const companies = (companiesData?.data ?? []) as Company[]
  const [gaps, setGaps] = useState<GapReport | null>(null)
  const [selected, setSelected] = useState<Record<string, Set<string>>>({})
  const [expandedRole, setExpandedRole] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, SeedResult> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const scan = async () => {
    setStep('scanning')
    setError(null)
    setSelected({})
    setResults(null)
    try {
      const params: Record<string, string> = {}
      if (roleType) params.role_type = roleType
      if (companyId) params.company_id = companyId
      const res = await flaskApi.get<GapReport>('/permission-seed/gaps', { params })
      setGaps(res.data)
      if (res.data.by_role_type.length === 0) {
        setStep('done')
        return
      }
      const initial: Record<string, Set<string>> = {}
      for (const rg of res.data.by_role_type) {
        const perms = new Set<string>()
        for (const m of rg.missing) {
          for (const p of m.permissions) perms.add(p)
        }
        initial[rg.role_type] = perms
      }
      setSelected(initial)
      setStep('review')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Scan failed'
      setError(msg)
      setStep('idle')
    }
  }

  const togglePerm = (roleType: string, perm: string) => {
    setSelected((prev) => {
      const next = { ...prev }
      const s = new Set(next[roleType])
      if (s.has(perm)) s.delete(perm)
      else s.add(perm)
      next[roleType] = s
      return next
    })
  }

  const toggleAllForRole = (roleType: string) => {
    setSelected((prev) => {
      const next = { ...prev }
      const current = next[roleType]
      const rg = gaps?.by_role_type.find((r) => r.role_type === roleType)
      if (!rg) return next
      if (current.size > 0) {
        next[roleType] = new Set()
      } else {
        const all = new Set<string>()
        for (const m of rg.missing) for (const p of m.permissions) all.add(p)
        next[roleType] = all
      }
      return next
    })
  }

  const seed = async () => {
    setStep('seeding')
    setError(null)
    const selections = Object.entries(selected)
      .filter(([, perms]) => perms.size > 0)
      .map(([role_type, perms]) => ({ role_type, permissions: Array.from(perms) }))
    try {
      const res = await flaskApi.post<Record<string, SeedResult>>('/permission-seed/seed', {
        selections,
        dry_run: false,
        company_id: companyId || undefined,
      })
      setResults(res.data)
      setStep('done')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { description?: string } } })?.response?.data?.description ?? 'Seed failed'
      setError(msg)
      setStep('review')
    }
  }

  const totalSelected = useMemo(() => {
    let count = 0
    for (const perms of Object.values(selected)) count += perms.size
    return count
  }, [selected])

  const selectedCompany = useMemo(() => companies.find((c) => c.id === companyId), [companies, companyId])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Permission Seeder</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Detect missing permissions and selectively seed them into role records.
        </p>
      </div>

      <div className="bento-card p-5 flex items-start gap-3">
        <ShieldCheck size={16} className="text-sky-500 shrink-0 mt-0.5" />
        <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
          <p>
            Compares the <span className="font-bold">canonical permission definitions</span> in{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px]">api/constants/roles_permission.py</code>{' '}
            against live database records.
          </p>
          <p>
            Only <span className="font-bold text-emerald-500">adds missing</span> permissions.
            Select individual permissions per role type to control exactly what gets seeded.
          </p>
        </div>
      </div>

      {step === 'idle' && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
          >
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
          <select
            value={roleType}
            onChange={(e) => setRoleType(e.target.value)}
            className="h-10 rounded-lg border border-border bg-background px-3 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
          >
            <option value="">All role types</option>
            {ROLE_TYPES.map((rt) => (
              <option key={rt} value={rt}>{rt}</option>
            ))}
          </select>
          <Button className="gap-2 bg-[#076698] hover:bg-[#055580]" size="lg" onClick={scan}>
            <Scan size={16} /> Scan for Missing Permissions
          </Button>
        </div>
      )}

      {step === 'scanning' && (
        <div className="bento-card p-8 flex items-center justify-center gap-3">
          <Loader2 size={20} className="animate-spin text-[#076698]" />
          <p className="text-sm text-slate-500">Scanning database records{selectedCompany ? ` for ${selectedCompany.company_name}` : ''}...</p>
        </div>
      )}

      {(step === 'review' || step === 'confirm' || step === 'seeding') && gaps && (
        <>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
            >
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
            <select
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
            >
              <option value="">All role types</option>
              {ROLE_TYPES.map((rt) => (
                <option key={rt} value={rt}>{rt}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" className="text-xs" onClick={scan} disabled={step === 'seeding'}>
              <Scan size={13} className="mr-1" /> Re-scan
            </Button>
            <Button
              size="sm" className="text-xs gap-1 bg-[#076698] hover:bg-[#055580] text-white"
              onClick={() => setStep('confirm')}
              disabled={totalSelected === 0 || step === 'seeding'}
            >
              <Play size={13} /> Seed {totalSelected} permissions
            </Button>
          </div>

          <div className="flex items-center gap-4 px-1">
            <Badge variant="destructive" className="text-xs">
              {gaps.by_role_type.length} role types with gaps
            </Badge>
            <span className="text-xs text-slate-400">{totalSelected} permissions selected</span>
          </div>

          <div className="space-y-2">
            {gaps.by_role_type.map((rg) => {
              const isExpanded = expandedRole === rg.role_type
              const roleSelection = selected[rg.role_type] ?? new Set()
              const allMissing = rg.missing.reduce((acc, m) => acc + m.permissions.length, 0)
              const selectedCount = roleSelection.size
              return (
                <div key={rg.role_type} className="bento-card overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors"
                    onClick={() => setExpandedRole(isExpanded ? null : rg.role_type)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-slate-700 dark:text-slate-200">{rg.role_type}</span>
                        <Badge variant="secondary" className="text-[9px]">{rg.role_count} record{rg.role_count !== 1 ? 's' : ''}</Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {allMissing} missing permissions · {selectedCount} selected
                      </p>
                    </div>
                    {isExpanded ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-border/30">
                      <div className="px-4 py-2 flex items-center gap-2 border-b border-border/20">
                        <button
                          type="button"
                          className="text-[10px] font-bold text-[#076698] hover:underline"
                          onClick={(e) => { e.stopPropagation(); toggleAllForRole(rg.role_type) }}
                        >
                          {selectedCount > 0 ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
                        {rg.missing.map((entry) => (
                          <div key={entry.category} className="space-y-1.5">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {entry.category}
                            </p>
                            <div className="space-y-1">
                              {entry.permissions.map((perm) => (
                                <label
                                  key={perm}
                                  className={cn(
                                    'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors',
                                    'hover:bg-sky-50 dark:hover:bg-sky-900/20',
                                    roleSelection.has(perm) && 'bg-sky-50 dark:bg-sky-900/20',
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={roleSelection.has(perm)}
                                    onChange={() => togglePerm(rg.role_type, perm)}
                                    className="w-3.5 h-3.5 rounded accent-[#076698] shrink-0"
                                  />
                                  <span className="text-xs text-slate-600 dark:text-slate-300 font-mono">{perm}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {step === 'confirm' && (
        <div className="bento-card p-5 space-y-4 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-extrabold text-amber-700 dark:text-amber-400">Confirm Seeding</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                This will add <span className="font-bold">{totalSelected} permissions</span> across{' '}
                <span className="font-bold">{Object.values(selected).filter((s) => s.size > 0).length} role types</span>.
                Existing permissions are never modified.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setStep('review')}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1 bg-[#076698] hover:bg-[#055580]" onClick={seed}>
              <Play size={13} /> Confirm &amp; Seed
            </Button>
          </div>
        </div>
      )}

      {step === 'seeding' && (
        <div className="bento-card p-8 flex items-center justify-center gap-3">
          <Loader2 size={20} className="animate-spin text-[#076698]" />
          <p className="text-sm text-slate-500">Applying permission changes...</p>
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-4">
          {results ? (
            <>
              <div className="flex items-start gap-2 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-xs">
                <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Seeding complete</p>
                </div>
              </div>
              <div className="bento-card p-5">
                <h3 className="text-sm font-extrabold text-slate-700 dark:text-slate-200 mb-4">Results by Role Type</h3>
                <div className="space-y-3">
                  {Object.entries(results).map(([rt, result]) => (
                    <div key={rt} className="flex items-center justify-between border-b border-border/30 pb-2 last:border-0 last:pb-0">
                      <div>
                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{rt}</span>
                        <span className="text-[10px] text-slate-400 ml-2">
                          {result.total_additions} permission{result.total_additions !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <Badge variant={result.roles_updated > 0 ? 'default' : 'secondary'} className="text-[10px]">
                        {result.roles_updated} role{result.roles_updated !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-xs">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
              <p className="font-bold">All permissions are in sync. No gaps found.</p>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
            >
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
            <select
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-2.5 text-sm text-slate-700 dark:text-slate-200 outline-none focus:border-[#076698] focus:ring-1 focus:ring-[#076698]/30"
            >
              <option value="">All role types</option>
              {ROLE_TYPES.map((rt) => (
                <option key={rt} value={rt}>{rt}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" className="text-xs" onClick={scan}>
              <Scan size={13} className="mr-1" /> Re-scan
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">
          <XCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
